import { isValidElement, useMemo } from 'react';
import { deepmerge } from '@mui/utils';
import { Box, Card, CardContent, CardHeader, Tooltip, Typography } from '@mui/material';
import { HelpOutlineOutlined as HelpOutlineOutlinedIcon } from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import Column from '../Column';
import Row from '../Row';
import SkeletonGroupedBarChartCard from './SkeletonGroupedBarChartCard';
import theme from '../../theme';

export default function LineChartCard(props: {
    label: string | null | undefined | React.ReactElement;
    tooltip?: string;
    chartOptions?: ApexOptions;
    chartSeries?: ApexAxisChartSeries | number[];
    noDataOverlay?: React.ReactElement;
    loading?: boolean;
}) {
    const { label, tooltip, chartOptions, chartSeries, noDataOverlay, loading = false } = props;

    const defaultChartOptions: ApexOptions = {
        chart: {
            height: 400,
            toolbar: {
                show: false,
            },
        },
        stroke: {
            // No stroke for the column series and a thicker stroke for the line series
            width: [0, 4],
        },
        plotOptions: {
            bar: {
                columnWidth: '50%',
                borderRadius: 4,
            },
        },
        markers: {
            size: [0, 3],
            strokeColors: [theme.palette.grey[300], theme.palette.primary.main],
            strokeWidth: 2,
            hover: { sizeOffset: 3 },
        },
        dataLabels: {
            enabled: false,
        },
        colors: [theme.palette.grey[300], theme.palette.primary.main],
        xaxis: {
            labels: {
                rotate: -45,
            },
        },
        yaxis: [
            {
                title: {
                    style: {
                        fontFamily: theme.typography.fontFamily,
                        fontSize: `${theme.typography.body2.fontSize}`,
                        fontWeight: theme.typography.body2.fontWeight,
                    },
                },
            },
            {
                opposite: true,
                title: {
                    style: {
                        fontFamily: theme.typography.fontFamily,
                        fontSize: `${theme.typography.body2.fontSize}`,
                        fontWeight: theme.typography.body2.fontWeight,
                    },
                },
            },
        ],
        tooltip: {
            shared: true,
            intersect: false,
            marker: {
                show: false,
            },
        },
        legend: {
            position: 'top',
            markers: {
                size: 14,
            },
        },
    };

    const hasData: boolean | undefined = useMemo(() => {
        return chartSeries && chartSeries.length > 0 ? true : false;
    }, [chartSeries]);

    const hasNoDataOverlay: boolean | undefined = useMemo(() => {
        return noDataOverlay !== null && noDataOverlay !== undefined;
    }, [noDataOverlay]);

    const boxHeight: number = 560;

    return (
        <Card variant="outlined">
            <CardHeader
                title={
                    <Row gap={0.5} alignItems="center">
                        {isValidElement(label) ? (
                            label
                        ) : (
                            <Typography variant="body1" color="text.secondary">
                                {label}
                            </Typography>
                        )}

                        {tooltip ? (
                            <Tooltip title={tooltip}>
                                <HelpOutlineOutlinedIcon color="action" sx={{ fontSize: 16 }} />
                            </Tooltip>
                        ) : (
                            ''
                        )}
                    </Row>
                }
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            />
            <CardContent>
                {loading ? (
                    <Column alignItems="center" justifyContent="center" sx={{ height: boxHeight + 2 }}>
                        <SkeletonGroupedBarChartCard />
                    </Column>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: hasData === true ? 'block' : 'none',
                            }}
                        >
                            <ReactApexChart
                                type="line"
                                options={deepmerge(defaultChartOptions, chartOptions)}
                                series={chartSeries}
                                height={boxHeight}
                                // width={500}
                            />
                        </Box>

                        {hasData === false && (
                            <>
                                {hasNoDataOverlay === true && <>{noDataOverlay}</>}

                                {hasNoDataOverlay === false && (
                                    <Column alignItems="center" justifyContent="center" sx={{ height: boxHeight + 2 }}>
                                        <Typography variant="body2">No data available</Typography>
                                    </Column>
                                )}
                            </>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
